/**
 * Utility functions for patient-related operations
 */

/**
 * Safely formats a patient DoB string (YYYY-MM-DD) for display.
 * Manually parses date components to avoid the UTC timezone off-by-one
 * bug that occurs with parseISO() + toLocaleDateString().
 */
export function formatDob(dob: string | null | undefined): string {
  if (!dob) return 'N/A';
  try {
    const [year, month, day] = dob.split('T')[0].split('-').map(Number);
    return `${month}/${day}/${year}`;
  } catch {
    return dob;
  }
}

/**
 * Formats a patient DoB with age in years, e.g. "5/15/1990 (35y)".
 */
export function formatDobWithAge(dob: string | null | undefined): string {
  if (!dob) return 'N/A';
  const formatted = formatDob(dob);
  if (formatted === 'N/A') return formatted;
  try {
    const [year, month, day] = dob.split('T')[0].split('-').map(Number);
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return `${formatted} (${age}y)`;
  } catch {
    return formatted;
  }
}

/**
 * Formats a patient's name with optional preferred name in quotes
 * @param firstName - Patient's first name
 * @param lastName - Patient's last name
 * @param preferredName - Patient's preferred name (optional)
 * @returns Formatted name string (e.g., "John Smith" or 'John "Johnny" Smith')
 */
export const formatPatientName = (
  firstName?: string | null,
  lastName?: string | null,
  preferredName?: string | null
): string => {
  const first = firstName || '';
  const last = lastName || '';
  const preferred = preferredName || '';

  if (preferred) {
    return `${first} "${preferred}" ${last}`.trim();
  }
  return `${first} ${last}`.trim();
};